import type { GameMode, MissionDifficulty } from '@/state/types';

/**
 * Phase 5 mission constants — generator + lifecycle.
 *
 * The generator + dispatch logic lives elsewhere (lib/missionGenerator.ts,
 * gameStore.tick). This file is the single source of truth for tunables.
 */

/** Reward in € per difficulty bucket (Wiki-aligned). */
export const MISSION_DIFFICULTY_REWARDS: Record<MissionDifficulty, number> = {
  easy: 15_000,
  medium: 25_000,
  hard: 45_000,
  expert: 75_000,
  // 10× medium — "Großeinsatz / Katastrophe"
  extreme: 150_000,
};

/** Spawn-time difficulty distribution. Sum must equal 1. */
export const MISSION_DIFFICULTY_WEIGHTS: Record<MissionDifficulty, number> = {
  easy: 0.5,
  medium: 0.3,
  hard: 0.15,
  expert: 0.04,
  extreme: 0.01,
};

/** Per-level reduction of spawn interval. Capped at 0.6× (40 % faster at L20). */
export const MISSION_INTERVAL_LEVEL_FACTOR = 0.02;

/** Hard cap on simultaneously active missions (open/dispatched/onscene). */
export const MAX_CONCURRENT_MISSIONS = 8;

/** How long a hospital slot stays occupied after Status 8. (5 game-minutes.) */
export const HOSPITAL_TREATMENT_MINUTES = 5;

export interface MissionPenaltyModifier {
  /** Penalty fraction of payout when a mission fails entirely. */
  failurePercent: number;
  /** Penalty fraction per dead patient. */
  deadPatientPercent: number;
  /** Hard timeout (in-game minutes) before a mission auto-fails. */
  timeoutMinutes: number;
}

export const MISSION_PENALTY_MODIFIERS: Record<GameMode, MissionPenaltyModifier> = {
  normal: { failurePercent: 0.25, deadPatientPercent: 0.15, timeoutMinutes: 360 },
  realistic: { failurePercent: 0.5, deadPatientPercent: 0.2, timeoutMinutes: 360 },
  sandbox: { failurePercent: 0.25, deadPatientPercent: 0.15, timeoutMinutes: 360 },
};

/**
 * Spawn-interval ranges per mode (real-seconds at 1× speed).
 * Sandbox uses a separate user-configurable value (sandboxConfig.missionIntervalSec).
 */
export interface MissionIntervalRange {
  minSec: number;
  maxSec: number;
}

export const MISSION_INTERVAL_RANGES: Record<
  Exclude<GameMode, 'sandbox'>,
  MissionIntervalRange
> = {
  normal: { minSec: 45, maxSec: 120 },
  realistic: { minSec: 20, maxSec: 60 },
};

/** Sandbox slider bounds. */
export const SANDBOX_INTERVAL_MIN_SEC = 5;
export const SANDBOX_INTERVAL_MAX_SEC = 300;
export const SANDBOX_INTERVAL_DEFAULT_SEC = 60;

/**
 * Phase 6.1 — Mission escalation.
 *
 * Cooldown gates two escalations on the same mission. Triggers per pair are
 * stored on each catalog entry (see missionCatalog). Timer is measured from
 * `lastEscalatedAt ?? spawnedAt` per user spec 2b.
 */
export const ESCALATION_COOLDOWN_MS = 60_000;

/** Half of the original remaining lifetime added to expiresAt on escalation. */
export const ESCALATION_TIMEOUT_BONUS_FACTOR = 0.5;

/** Progress threshold below which F3→F4 escalates while in Status 4. */
export const ESCALATION_PROGRESS_FLOOR = 0.5;
